import {
  parseShoppingList,
  shoppingOfferExpired,
  type ShoppingListItem,
} from "./shopping-list";
import type { AppLocale } from "./locale";
import { SHOPPING_SHARE_PHOTO_LIMIT, type ShoppingShareScope } from "./shopping-sharing";

const SHARED_START_PATTERN = /^list_([a-f\d]{36})$/;
const SHARED_ITEM_START_PATTERN = /^listitem_([a-f\d]{36})_(0|[1-9]\d?)$/;
export const MAX_SHARED_LIST_ITEM_INDEX = 99;

export interface SharedListItemStartParameter {
  token: string;
  index: number;
}

export interface SharedListLiveProductIdentity {
  key: string;
  store: string;
  validUntil: string;
}

export type SharedListItemLanding =
  | { kind: "invalid"; item: null }
  | { kind: "expired" | "snapshot" | "live"; item: ShoppingListItem };

export interface PreparedShoppingListShare {
  preparedMessageId: string;
  preparedMessageExpiresAt: number | null;
  shareUrl: string;
  expiresAt: string;
  photosAttached: number;
  photoPreviewLeft: boolean;
  fallbackText?: string;
}

export interface SharedShoppingList {
  createdAt: string;
  expiresAt: string;
  items: ShoppingListItem[];
}

export class SharedListApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
  ) {
    super(code);
  }
}

async function responseBody(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function parseSharedListStartParameter(value: string | null): string | null {
  if (!value) {
    return null;
  }
  return SHARED_START_PATTERN.exec(value)?.[1] ?? null;
}

export function sharedListItemStartParameter(
  token: string,
  index: number,
): string | null {
  if (
    !/^[a-f\d]{36}$/.test(token) ||
    !Number.isSafeInteger(index) ||
    index < 0 ||
    index > MAX_SHARED_LIST_ITEM_INDEX
  ) {
    return null;
  }
  return `listitem_${token}_${index}`;
}

export function parseSharedListItemStartParameter(
  value: string | null,
): SharedListItemStartParameter | null {
  if (!value) {
    return null;
  }
  const match = SHARED_ITEM_START_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const index = Number(match[2]);
  return index <= MAX_SHARED_LIST_ITEM_INDEX
    ? { token: match[1]!, index }
    : null;
}

export function sharedListItemTelegramUrl(
  sharedListUrl: string,
  token: string,
  index: number,
): string | null {
  const startParameter = sharedListItemStartParameter(token, index);
  if (!startParameter) {
    return null;
  }
  try {
    const url = new URL(sharedListUrl);
    if (url.protocol !== "https:" || url.hostname.toLowerCase() !== "t.me") {
      return null;
    }
    if (url.searchParams.get("startapp") !== `list_${token}`) {
      return null;
    }
    url.searchParams.set("startapp", startParameter);
    return url.toString();
  } catch {
    return null;
  }
}

export function resolveSharedListItemLanding(
  items: readonly ShoppingListItem[],
  index: number,
  today: string,
  liveProduct: SharedListLiveProductIdentity | null,
): SharedListItemLanding {
  if (
    !Number.isSafeInteger(index) ||
    index < 0 ||
    index >= items.length ||
    index > MAX_SHARED_LIST_ITEM_INDEX
  ) {
    return { kind: "invalid", item: null };
  }
  const item = items[index]!;
  if (shoppingOfferExpired(item, today)) {
    return { kind: "expired", item };
  }
  if (
    liveProduct?.key === item.key &&
    liveProduct.store === item.store &&
    liveProduct.validUntil === item.validUntil &&
    liveProduct.validUntil >= today
  ) {
    return { kind: "live", item };
  }
  return { kind: "snapshot", item };
}

export async function prepareShoppingListShare(
  initData: string,
  items: ShoppingListItem[],
  locale: AppLocale,
  scope?: ShoppingShareScope,
  withPhotos = false,
): Promise<PreparedShoppingListShare> {
  const response = await fetch("/api/shared-lists", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData, items, locale, ...(scope ? { scope } : {}), ...(scope && withPhotos ? { withPhotos: true } : {}) }),
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new SharedListApiError(
      typeof body.error === "string" ? body.error : "share_failed",
      response.status,
    );
  }
  if (
    typeof body.preparedMessageId !== "string" ||
    typeof body.shareUrl !== "string" ||
    typeof body.expiresAt !== "string"
  ) {
    throw new SharedListApiError("invalid_share_response", 502);
  }
  return {
    preparedMessageId: body.preparedMessageId,
    preparedMessageExpiresAt:
      typeof body.preparedMessageExpiresAt === "number"
        ? body.preparedMessageExpiresAt
        : null,
    shareUrl: body.shareUrl,
    expiresAt: body.expiresAt,
    photosAttached: Number.isInteger(body.photosAttached) && Number(body.photosAttached) >= 0 && Number(body.photosAttached) <= SHOPPING_SHARE_PHOTO_LIMIT ? Number(body.photosAttached) : 0,
    photoPreviewLeft: body.photoPreviewLeft === true,
    ...(body.degraded === true && typeof body.fallbackText === "string"
      ? { fallbackText: body.fallbackText } : {}),
  };
}

export async function loadSharedShoppingList(
  token: string,
): Promise<SharedShoppingList> {
  const response = await fetch(`/api/shared-lists/${encodeURIComponent(token)}`, {
    cache: "no-store",
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new SharedListApiError(
      typeof body.error === "string" ? body.error : "shared_list_failed",
      response.status,
    );
  }
  const sourceItems = Array.isArray(body.items) ? body.items : [];
  const items = parseShoppingList(
    JSON.stringify({ version: 1, items: sourceItems }),
  );
  if (
    items.length === 0 ||
    items.length !== sourceItems.length ||
    typeof body.createdAt !== "string" ||
    typeof body.expiresAt !== "string"
  ) {
    throw new SharedListApiError("invalid_shared_list_response", 502);
  }
  return {
    createdAt: body.createdAt,
    expiresAt: body.expiresAt,
    items,
  };
}
